import { PersonPin } from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import React, { memo, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import Baseline from "../../../../components/form/Baseline";
import {
  getPolicyCurrentValue,
  isFixedRoutePolicy,
} from "../../../../pages/admin/route-policies/utils";
import { selectAccountCurrency } from "../../../../store/features/accountSlice";
import {
  getRoutePolicies,
  selectRoutePoliciesByRole,
} from "../../../../store/features/configs/routePoliciesSlice";
import formatAmount from "../../../../utils/formatAmount";
import getRateByDate from "../../../../utils/getRateByDate";
import { curr } from "../../../../utils/more/currency_country";
import SelectorBox from "../SelectorBox";

const renderOptionRate = ({ option, currency }) => {
  const isFixed = isFixedRoutePolicy(option);
  const value = isFixed ? getPolicyCurrentValue(option) : option?.currentRate;
  return (
    <Typography
      fontWeight={"600"}
      color="primary"
      component={"span"}
      variant="inherit"
    >
      {curr(currency)} {formatAmount(value)}
      {!isFixed && " /km"}
    </Typography>
  );
};

function RoutePoliciesInput({
  value,
  readOnly,
  onChange = () => {},
  size,
  variant = "filled",
  inputRef,
  role = "personal",
  isEditable = true,
  margin = "dense",
  date = new Date(),
  fixedOnly = false,
}) {
  const dispatch = useDispatch();

  const { data, status } = useSelector((state) =>
    selectRoutePoliciesByRole(state, role),
  );

  const currency = useSelector(selectAccountCurrency);
  const loading = status === "loading";

  const options = useMemo(() => {
    const baseList = data || [];
    const filtered = fixedOnly
      ? baseList.filter((item) => isFixedRoutePolicy(item))
      : baseList;
    return filtered.map((item) => ({
      ...item,
      // currentRate é usado pelo cálculo das políticas por km (varia por data).
      // Para rota fixa mantemos o currentRate original (não vem do history).
      currentRate: isFixedRoutePolicy(item)
        ? getPolicyCurrentValue(item)
        : getRateByDate(item?.history || [], new Date(date)),
    }));
  }, [data, date, fixedOnly]);

  const onOpen = () => {
    dispatch(getRoutePolicies(role));
  };

  const isValueFixed = isFixedRoutePolicy(value);

  if (!isEditable) {
    return (
      <Baseline
        Icon={PersonPin}
        label={"Taxa"}
        value={
          value?.currentRate
            ? `${curr(currency)} ${formatAmount(value?.currentRate)}${
                isValueFixed ? "" : " /km"
              } - ${value?.name}`
            : ""
        }
        emptyLabel={"Não informado"}
      />
    );
  } else {
    return (
      <SelectorBox Icon={PersonPin} active={Boolean(value)}>
        <Autocomplete
          size={size || "small"}
          autoHighlight
          fullWidth
          openOnFocus
          readOnly={readOnly}
          disableClearable={fixedOnly}
          options={options || []}
          loading={loading}
          value={value || null}
          onOpen={onOpen}
          onChange={(e, v) => {
            onChange(v);
          }}
          isOptionEqualToValue={(option, value) => option?._id === value?._id}
          getOptionLabel={(option) => {
            const isFixed = isFixedRoutePolicy(option);
            return `${curr(currency)} ${formatAmount(isFixed ? option?.preDefinedRoute?.amount : option?.currentRate)}${
              isFixed ? "" : " /km"
            } - ${option?.name}`;
          }}
          noOptionsText={
            fixedOnly
              ? "Nenhuma rota predefinida disponível"
              : "Nenhuma política encontrada"
          }
          renderOption={(props, option, { selected }) => {
            const isFixed = isFixedRoutePolicy(option);
            return (
              <MenuItem
                {...props}
                key={option?._id}
                selected={selected}
                sx={{ minHeight: 70, py: 1 }}
              >
                <Box flex={1} minWidth={0}>
                  <Box display={"flex"} alignItems={"center"} gap={1}>
                    <Typography
                      fontWeight={"600"}
                      variant="inherit"
                      noWrap
                      sx={{ flex: 1, minWidth: 0 }}
                    >
                      {option?.name}
                    </Typography>
                    {isFixed && (
                      <Chip
                        size="small"
                        label={"Rota fixa"}
                        color="secondary"
                        variant="outlined"
                        sx={{ height: 18, fontSize: ".68rem" }}
                      />
                    )}
                  </Box>
                  {isFixed && option?.preDefinedRoute && (
                    <Typography
                      fontSize={".75rem"}
                      color="text.secondary"
                      noWrap
                    >
                      {option?.preDefinedRoute?.point1} →{" "}
                      {option?.preDefinedRoute?.point2}
                    </Typography>
                  )}
                  <Typography color={"text.secondary"} variant="body2">
                    {isFixed ? "Valor: " : "Taxa: "}
                    {renderOptionRate({ option, currency })}
                  </Typography>
                </Box>
              </MenuItem>
            );
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              name="routePolicy"
              margin={margin}
              inputRef={inputRef}
              multiline
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <React.Fragment>
                    {loading ? (
                      <CircularProgress color="inherit" size={15} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </React.Fragment>
                ),
              }}
              fullWidth
              variant={variant}
              placeholder={
                fixedOnly ? "Selecionar rota predefinida" : "Selecionar taxa"
              }
            />
          )}
        />
      </SelectorBox>
    );
  }
}

export default memo(RoutePoliciesInput);
